// ------------------------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';
import escapeStringRegExp from 'escape-string-regexp';
// ------------------------------------------------------------------------------------------------
// internal library
import { MUISearchBar, MUISearchBarProps } from './MUISearchBar';
import { Localization } from './Localization/Localization';
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { DebugEx } from '@Utils/DebugEx';

// material-ui components
import { List, ListItem } from 'material-ui/List';
import { AutoComplete } from 'material-ui';
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';
import Toggle from 'material-ui/Toggle';

import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import SearchIcon from 'material-ui/svg-icons/action/search';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import { SearchBarGetLocalizationDictionary, SearchBarLocalizationTextMapKey, SearchBarUILabelKey } from './SearchBarLocalization';
// ------------------------------------------------------------------------------------------------
export interface SearchBarProps extends React.Props<SearchBar> {
    /**
     * Style
     *
     * @type {React.CSSProperties}
     * @memberof SearchBarProps
     */
    style: React.CSSProperties;

    /**
     * Search's source
     *
     * @type {{ key: string, value: string }[]}
     * @memberof SearchBarProps
     */
    source: { key: string, value: string }[];

    /**
     * Style
     *
     * @type {(keys: string[], emptyValue: boolean) => void}
     * @memberof SearchBarProps
     */
    onSearch: (keys: string[], emptyValue: boolean, searchValue: string) => void;

    /**
     * If true, search icon won't be overloaded
     *
     * @type {boolean}
     * @memberof SearchBarProps
     */
    simple?: boolean;

    /**
     * Color used for settings/clear-search icons.
     *
     * @type {string}
     * @memberof SearchBarProps
     */
    iconColor?: string;

    /**
     * Text color used text input when in focus.
     *
     * @type {string}
     * @memberof SearchBarProps
     */
    activeTextColor?: string;

    /**
     * Text color used text input when NOT in focus.
     *
     * @type {string}
     * @memberof SearchBarProps
     */
    inactiveTextColor?: string;

    /**
     * todo: add description
     *
     * @type {boolean}
     * @memberof SearchBarProps
     */
    showResults?: boolean;

    /**
     * Hide the search icon to increase the size of textbox
     *
     * @type {boolean}
     * @memberof SearchBarProps
     */
    hideSearchIcon?: boolean;

    /**
     * Optional key for applying localization.
     */
    localizationKey?: string;

    /**
     * Optional focus textfield after X miliseconds.
     */
    autoFocusTimeout?: number;

    /**
     * Optional search value
     */
    defaultSearchValue?: string;

    /**
     * Override the inline-styles of the button element.
     *
     * @type {React.CSSProperties}
     * @memberof MUISearchBarProps
     */
    iconButtonStyle?: React.CSSProperties;
}

export interface SearchBarState {
    searchValue: string;
    isRegExpValid: boolean;
    useRegExp: boolean;
    matchCase: boolean;
    matchWholeWord: boolean;
    dataSource: string[];
}
// ------------------------------------------------------------------------------------------------
export class SearchBar extends React.Component<SearchBarProps, SearchBarState> {
    private _innerComponent: MUISearchBar;
    public static defaultProps: SearchBarProps = {
        style: {},
        onSearch: () => { },
        simple: false,
        showResults: false,
        source: [],
        iconColor: 'black',
        activeTextColor: 'black',
        inactiveTextColor: 'rgba(0,0,0,0.2)',
        defaultSearchValue: null
    };
    // ------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key. 
     */
    private static _GetLocalizedLabels(localizationKey: string): any {
        // get default values
        const defaultValues = SearchBarGetLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("SearchBar: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(SearchBarLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("SearchBar: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------
    constructor(props: SearchBarProps) {
        super(props);
        this.state = this.getInitialState(this.props);
    }
    // ------------------------------------------------------------------------------------------------
    public getInitialState(props: SearchBarProps): SearchBarState {
        return {
            searchValue: props.defaultSearchValue ?? '',
            matchCase: false,
            matchWholeWord: false,
            useRegExp: false,
            isRegExpValid: true,
            dataSource: _.map(this.props.source, s => s.value)
        };
    }
    // ------------------------------------------------------------------------------------------------
    private getFilteredSource(searchValue: string) {
        let filteredKeys: string[] = [];

        try {
            if (!IsNullOrWhiteSpace(searchValue)) {
                // If user regular expression is set escape special characters.
                if (!this.state.useRegExp) {
                    searchValue = escapeStringRegExp(searchValue);
                }

                // Create regular expression.
                let regExp: RegExp = new RegExp(
                    (this.state.matchWholeWord ? '\\b' : '') + searchValue + (this.state.matchWholeWord ? '\\b' : ''),
                    this.state.matchCase ? '' : 'i'
                );

                // Filter dashboards by created regular expession.
                _.forEach(this.props.source, (kvpair, index) => {
                    if (kvpair.value.search(regExp) > -1) {
                        filteredKeys.push(kvpair.key);
                    }
                });
            }
        }
        catch (err) {
            console.log(err.message);
            console.log(err.stack);
        }

        return filteredKeys;
    }
    // ------------------------------------------------------------------------------------------------
    public focus() {
        if (isDefined(this._innerComponent))
            try {
                this._innerComponent.focus();
            }
            catch (ex) {
                DebugEx.TraceError(ex);
            }
    }
    // ------------------------------------------------------------------------------------------------
    public blur() {
        if (isDefined(this._innerComponent))
            try {
                this._innerComponent.blur();
            }
            catch (ex) {
                DebugEx.TraceError(ex);
            }
    }
    // ------------------------------------------------------------------------------------------------
    componentDidMount() {
        const { autoFocusTimeout } = this.props;
        if (isDefined(autoFocusTimeout) && autoFocusTimeout > 0) {
            setTimeout(() => this.focus(), autoFocusTimeout);
        }
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const { localizationKey,
            style, iconButtonStyle,
            hideSearchIcon,
            iconColor, activeTextColor, inactiveTextColor,
            showResults, onSearch,
            simple } = this.props;
        const localizedLabels = SearchBar._GetLocalizedLabels(localizationKey)
        const props: MUISearchBarProps = {
            style: isDefined(style) ? style : {},
            iconButtonStyle: isDefined(iconButtonStyle) ? iconButtonStyle : {},
            closeIcon: hideSearchIcon === true ? null : <CloseIcon color={iconColor} />,
            searchIcon: hideSearchIcon === true ? null : <SearchIcon color={iconColor} />,
            value: this.state.searchValue,
            activeTextColor: activeTextColor,
            inactiveTextColor: inactiveTextColor,
            dataSource: (showResults) ? this.state.dataSource : [],
            filter: this.state.matchCase ? AutoComplete.caseSensitiveFilter : AutoComplete.caseInsensitiveFilter,
            onChange: (value) => {
                let searchValue: string = value;
                let isRegExpValid = true;
                // Check if given regular expression is valid.
                if (this.state.useRegExp) {
                    try {
                        new RegExp(searchValue);
                        isRegExpValid = true;
                    }
                    catch (err) {
                        isRegExpValid = false;
                    }
                }

                // Update UI
                this.setState({
                    searchValue: searchValue,
                    isRegExpValid: isRegExpValid
                }, () => {
                    try {
                        if (isDefined(onSearch)) {
                            onSearch(
                                this.getFilteredSource(searchValue),
                                IsNullOrEmpty(searchValue),
                                searchValue
                            );
                        }
                    } catch (ex) {
                        console.log(ex.stack);
                        DebugEx.TraceError(ex);
                    }
                });
            },
            onRequestSearch: () => { },
            hintText: localizedLabels[SearchBarUILabelKey.HintText]
        };

        if (simple)
            return (
                <MUISearchBar
                    ref={(r) => { this._innerComponent = r; }}
                    {...props}
                />
            );
        else
            return (
                <MUISearchBar
                    ref={(r) => { this._innerComponent = r; }}
                    {...props}
                    extraMenuItems={
                        <IconMenu
                            style={{
                                width: 48,
                                height: 48
                            }}
                            key={'search-options-icon-menu'}
                            iconButtonElement={
                                <IconButton
                                    key={"search-options-button"}
                                    touch={true}
                                    tooltip={localizedLabels[SearchBarUILabelKey.Options]}
                                    tooltipPosition="bottom-left">
                                    <MoreVertIcon color={iconColor} />
                                </IconButton>
                            }
                        >
                            <List key={'search-options-list'}>
                                <ListItem
                                    key={'use-regex-option'}
                                    primaryText={localizedLabels[SearchBarUILabelKey.UseRegEx]}
                                    rightToggle={
                                        <Toggle
                                            toggled={this.state.useRegExp}
                                            onToggle={() => {
                                                this.setState({ useRegExp: !this.state.useRegExp });
                                            }}
                                        />}
                                />
                                <ListItem
                                    key={'match-case-option'}
                                    primaryText={localizedLabels[SearchBarUILabelKey.MatchCase]}
                                    rightToggle={
                                        <Toggle
                                            toggled={this.state.matchCase}
                                            onToggle={() => {
                                                this.setState({ matchCase: !this.state.matchCase });
                                            }}
                                        />}
                                />
                                <ListItem
                                    key={'match-whole-word-option'}
                                    primaryText={localizedLabels[SearchBarUILabelKey.MatchWholeWord]}
                                    rightToggle={
                                        <Toggle
                                            toggled={this.state.matchWholeWord}
                                            onToggle={() => {
                                                this.setState({ matchWholeWord: !this.state.matchWholeWord });
                                            }}
                                        />}
                                />
                            </List>
                        </IconMenu>
                    }
                />
            );
    }
}
// ------------------------------------------------------------------------------------------------